/**
 * LA SIMULATION D'UNE EXPÉRIENCE ALÉATOIRE — atelier de figures, palier L5,
 * F11 (`docs/pilotage/atelier-figures/CONCEPTION.md` § 2 et § 3).
 *
 * Un exercice qui déclare une EXPÉRIENCE (une roulette, des dés, un tirage, une
 * pièce) laisse l'élève la lancer 1, 10, 100 ou 1 000 fois et regarder la
 * fréquence relative d'un événement se rapprocher de sa probabilité.
 *
 * ─── Rien n'est noté ────────────────────────────────────────────────────────
 *
 * La simulation est une EXPLORATION : personne ne la corrige. La question qui
 * suit se corrige par sa voie normale (un nombre, un choix, une démarche). La
 * série est seulement JOINTE à la réponse, pour que le tuteur la voie.
 *
 * ─── Une graine, et la même série partout ───────────────────────────────────
 *
 * Le tirage est fait par l'appareil, avec une GRAINE qu'il choisit. Le
 * générateur (mulberry32) et la façon de tirer une issue sont écrits ici, en
 * arithmétique entière : la même graine donne la même série sur le téléphone,
 * au serveur et dans la console. Le serveur ne reçoit que la graine et le
 * nombre de lancers ; il REJOUE la série pour en garder les effectifs, et le
 * tuteur peut la rejouer à son tour.
 *
 * ─── Jamais un pourcentage ──────────────────────────────────────────────────
 *
 * Une fréquence s'écrit en fraction (« 27/100 ») ou en décimal (« 0,27 »),
 * comme l'exercice le déclare (`frequences`). Une probabilité s'écrit telle
 * que l'auteur l'a écrite, exacte.
 */
import { lireRationnel } from '../math/ensembles.js';
/* ─────────────────────────────── le format ──────────────────────────────── */
/** Ce que l'appareil fait tourner. Le genre ne change que le DESSIN, jamais le tirage. */
export const GENRES_D_EXPERIENCE = ['roulette', 'des', 'tirage', 'piece'];
/** Comment une fréquence relative s'écrit à l'élève. */
export const ECRITURES_DE_FREQUENCE = ['fraction', 'decimal'];
/** Les séries qu'on propose de lancer d'un coup (« Lancer 10 fois »). */
export const LANCERS_PROPOSES = [1, 10, 100, 1000];
/** Au-delà, la série n'est plus une exploration, et le serveur refuse de la rejouer. */
export const MAX_LANCERS = 100_000;
/** Une graine est un entier non signé de 32 bits. */
export const MAX_GRAINE = 4_294_967_295;
/** De deux à douze issues : la somme de deux dés en a onze. */
export const ISSUES_D_EXPERIENCE = { min: 2, max: 12 };
/* ─────────────────────────────── les griefs ─────────────────────────────── */
const pgcd = (a, b) => (b === 0 ? Math.abs(a) : pgcd(b, a % b));
const ppcm = (a, b) => (a / pgcd(a, b)) * b;
/** Ce qu'un schéma ne voit pas d'une expérience. Vide : elle se simule. */
export function griefsDExperience(e) {
    const g = [];
    if (e.issues.length < ISSUES_D_EXPERIENCE.min || e.issues.length > ISSUES_D_EXPERIENCE.max) {
        g.push(`une expérience a de ${ISSUES_D_EXPERIENCE.min} à ${ISSUES_D_EXPERIENCE.max} issues`);
    }
    const noms = e.issues.map((i) => i.nom);
    if (new Set(noms).size !== noms.length)
        g.push('deux issues portent le même nom');
    let somme = { n: 0, d: 1 };
    e.issues.forEach((issue, k) => {
        const r = lireRationnel(issue.p);
        if (r === null || r.n <= 0 || r.n > r.d) {
            g.push(`issue ${k + 1} (« ${issue.nom} ») : une probabilité strictement positive, au plus 1`);
            return;
        }
        const d = ppcm(somme.d, r.d);
        somme = { n: somme.n * (d / somme.d) + r.n * (d / r.d), d };
    });
    if (g.length === 0 && somme.n !== somme.d) {
        g.push('les probabilités des issues somment à 1');
    }
    if (e.evenement !== undefined) {
        if (e.evenement.issues.length === 0)
            g.push('un événement compte au moins une issue');
        for (const nom of e.evenement.issues) {
            if (!noms.includes(nom))
                g.push(`l’événement nomme « ${nom} », qui n’est pas une issue`);
        }
        if (new Set(e.evenement.issues).size !== e.evenement.issues.length) {
            g.push('l’événement nomme deux fois la même issue');
        }
    }
    /* Le tirage se fait en entiers, sur le dénominateur commun : il doit rester exact. */
    const commun = e.issues.reduce((d, i) => ppcm(d, lireRationnel(i.p)?.d ?? 1), 1);
    if (commun > 1_000_000)
        g.push('les probabilités ont un dénominateur commun trop grand');
    return g;
}
/* ─────────────────────────────── le tirage ──────────────────────────────── */
/**
 * Le générateur pseudo-aléatoire (mulberry32), en arithmétique 32 bits : le
 * même sur tous les moteurs JavaScript. Rend un entier de 0 à 2³² − 1.
 */
export function generateur(graine) {
    let a = graine >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return (t ^ (t >>> 14)) >>> 0;
    };
}
/** Les probabilités ramenées au dénominateur commun : des entiers, en cumul. */
function cumulsDe(e) {
    const lues = e.issues.map((i) => lireRationnel(i.p) ?? { n: 0, d: 1 });
    const d = lues.reduce((acc, r) => ppcm(acc, r.d), 1);
    let cumul = 0;
    const cumuls = lues.map((r) => {
        cumul += r.n * (d / r.d);
        return cumul;
    });
    return { cumuls, d };
}
export function creerSimulation(e, graine) {
    const suivant = generateur(graine);
    const { cumuls, d } = cumulsDe(e);
    const dernier = cumuls.length - 1;
    return {
        graine: graine >>> 0,
        tirer(n) {
            const sortie = [];
            for (let k = 0; k < n; k += 1) {
                /* Un entier de 0 à d − 1, sans biais visible : 2³² est très grand devant d. */
                const u = Math.floor((suivant() / 4294967296) * d);
                let i = 0;
                while (i < dernier && u >= cumuls[i])
                    i += 1;
                sortie.push(i);
            }
            return sortie;
        },
    };
}
/** La série ENTIÈRE d'une graine : les `lancers` premiers tirages. */
export function serieSimulee(e, graine, lancers) {
    return creerSimulation(e, graine).tirer(lancers);
}
/** L'événement suivi : son nom, les rangs de ses issues, sa probabilité exacte. */
export function evenementDe(e) {
    const noms = e.evenement !== undefined ? e.evenement.issues : [e.issues[0]?.nom ?? ''].filter(Boolean);
    const rangs = new Set(noms.map((nom) => e.issues.findIndex((i) => i.nom === nom)).filter((i) => i >= 0));
    let p = { n: 0, d: 1 };
    for (const i of rangs) {
        const r = lireRationnel(e.issues[i]?.p ?? '0') ?? { n: 0, d: 1 };
        const d = ppcm(p.d, r.d);
        p = { n: p.n * (d / p.d) + r.n * (d / r.d), d };
    }
    const g = pgcd(p.n, p.d) || 1;
    return {
        nom: e.evenement?.nom ?? `obtenir « ${e.issues[0]?.nom ?? ''} »`,
        rangs,
        p: { n: p.n / g, d: p.d / g },
    };
}
/** Le résumé d'une série, tel que l'essai le garde : rejoué depuis la graine. */
export function resumeDeSerie(e, graine, lancers) {
    const serie = serieSimulee(e, graine, lancers);
    const effectifs = e.issues.map(() => 0);
    for (const i of serie)
        effectifs[i] = (effectifs[i] ?? 0) + 1;
    const ev = evenementDe(e);
    const effectif = [...ev.rangs].reduce((s, i) => s + (effectifs[i] ?? 0), 0);
    return {
        graine: graine >>> 0,
        lancers,
        issues: e.issues.map((i) => i.nom),
        effectifs,
        evenement: { nom: ev.nom, effectif, p: ecrireProbabilite(ev.p, e) },
        frequences: e.frequences ?? 'decimal',
    };
}
/* ─────────────────────────────── les écritures ──────────────────────────── */
/** Un décimal à la française, au plus `chiffres` décimales. */
function decimalFr(v, chiffres) {
    const facteur = 10 ** chiffres;
    return String(Math.round(v * facteur) / facteur).replace('.', ',');
}
/**
 * Une FRÉQUENCE RELATIVE, écrite comme l'exercice le veut : « 27/100 » ou
 * « 0,27 ». Un décimal qui ne tombe pas juste s'écrit arrondi au millième,
 * précédé de « ≈ ». Jamais un pourcentage.
 */
export function ecrireFrequence(effectif, lancers, ecriture = 'decimal') {
    if (lancers <= 0)
        return '—';
    if (ecriture === 'fraction')
        return `${effectif}/${lancers}`;
    const v = effectif / lancers;
    const exact = Math.round(v * 1000) / 1000 === v;
    return `${exact ? '' : '≈ '}${decimalFr(v, 3)}`;
}
/** La probabilité de l'événement, écrite dans la même écriture que les fréquences. */
export function ecrireProbabilite(p, e) {
    if ((e.frequences ?? 'decimal') === 'fraction' || p.d === 1) {
        return p.d === 1 ? String(p.n) : `${p.n}/${p.d}`;
    }
    const v = p.n / p.d;
    const exact = Math.round(v * 1000) / 1000 === v;
    return `${exact ? '' : '≈ '}${decimalFr(v, 3)}`;
}
/** « 1 lancer », « 1 000 lancers » : l'espace insécable des milliers. */
export function ecrireLancers(n) {
    const ecrit = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${ecrit} ${n > 1 ? 'lancers' : 'lancer'}`;
}
/**
 * LA COURBE DES FRÉQUENCES : après chaque lancer, la fréquence relative de
 * l'événement. Au plus `points` points, pris régulièrement — une série de
 * 10 000 lancers se dessine en 200 points.
 */
export function courbeDesFrequences(serie, rangs, points = 200) {
    const sortie = [];
    const pas = Math.max(1, Math.ceil(serie.length / points));
    let compte = 0;
    serie.forEach((i, k) => {
        if (rangs.has(i))
            compte += 1;
        const n = k + 1;
        if (n % pas === 0 || n === serie.length)
            sortie.push({ n, f: compte / n });
    });
    return sortie;
}
/** La série dite en une phrase, pour le tuteur et pour un lecteur d'écran. */
export function direSerie(s) {
    const parIssue = s.issues.map((nom, i) => `${nom} : ${s.effectifs[i] ?? 0}`).join(' ; ');
    return (`${ecrireLancers(s.lancers)}. ${parIssue}. Fréquence de l’événement « ${s.evenement.nom} » : ` +
        `${ecrireFrequence(s.evenement.effectif, s.lancers, s.frequences)} (probabilité ${s.evenement.p}).`);
}
//# sourceMappingURL=simulation.js.map